import { supabase } from './supabase'

/** Local date, not UTC — toISOString() rolls over a day early in Vancouver. */
export const todayISO = () => new Date().toLocaleDateString('en-CA')

/** ISO weekday: 1 = Monday … 7 = Sunday. */
export const isoWeekday = (d = new Date()) => d.getDay() || 7

export const SHIFTS = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'night', label: 'Night' },
  { value: 'any', label: 'Any shift' },
]

export const DAY_LABELS = [
  { value: 1, short: 'M', label: 'Monday' },
  { value: 2, short: 'T', label: 'Tuesday' },
  { value: 3, short: 'W', label: 'Wednesday' },
  { value: 4, short: 'T', label: 'Thursday' },
  { value: 5, short: 'F', label: 'Friday' },
  { value: 6, short: 'S', label: 'Saturday' },
  { value: 7, short: 'S', label: 'Sunday' },
]

export const ASSIGNED_ROLES = [
  { value: 'caregiver', label: 'Caregiver' },
  { value: 'family_member', label: 'Family member' },
  { value: 'any', label: 'Anyone' },
]

/** Offered on first setup so a coordinator isn't staring at an empty list. */
export const DEFAULT_TEMPLATES = [
  { title: 'Morning medications', shift: 'morning' },
  { title: 'Breakfast assistance', shift: 'morning' },
  { title: 'Personal hygiene', shift: 'morning' },
  { title: 'Afternoon check-in', shift: 'afternoon' },
  { title: 'Evening medications', shift: 'evening' },
  { title: 'Dinner assistance', shift: 'evening' },
  { title: 'Bedtime routine', shift: 'night' },
]

/** Time of day → the shift a caregiver is most likely working. */
export function currentShift(now = new Date()) {
  const h = now.getHours()
  if (h >= 6 && h < 12) return 'morning'
  if (h >= 12 && h < 17) return 'afternoon'
  if (h >= 17 && h < 21) return 'evening'
  return 'night'
}

/**
 * Create today's tasks from any active template scheduled for today.
 *
 * Idempotent by title: a template whose title already exists among today's
 * tasks is skipped, so this is safe to call on every load.
 */
export async function generateDailyTasksFromTemplates(careRecipientId) {
  if (!careRecipientId) return { created: 0 }

  const day = isoWeekday()
  const today = todayISO()

  const { data: templates, error: tErr } = await supabase
    .from('task_templates')
    .select('*')
    .eq('care_recipient_id', careRecipientId)
    .eq('is_active', true)
    .contains('days_of_week', [day])

  // Table not migrated yet, or no read access — nothing to do, never throw.
  if (tErr || !templates?.length) return { created: 0 }

  const { data: existing } = await supabase
    .from('tasks')
    .select('title')
    .eq('care_recipient_id', careRecipientId)
    .eq('due_date', today)

  const seen = new Set((existing || []).map((t) => t.title))

  // created_by is NOT NULL, and getUser() is async — awaiting it matters.
  const { data: userData } = await supabase.auth.getUser()
  const userId = userData?.user?.id
  if (!userId) return { created: 0 }

  const toCreate = templates
    .filter((t) => !seen.has(t.title))
    .map((t) => ({
      care_recipient_id: careRecipientId,
      created_by: userId,
      title: t.title,
      description: t.description || null,
      due_date: today,
      is_completed: false,
      shift: t.shift || 'any',
    }))

  if (!toCreate.length) return { created: 0 }

  const { error } = await supabase.from('tasks').insert(toCreate)
  if (!error) return { created: toCreate.length }

  // The shift column arrives in its own migration. If the app is deployed
  // before that runs, retry without it rather than losing the day's tasks.
  if (isMissingColumn(error, 'shift')) {
    const withoutShift = toCreate.map(({ shift, ...rest }) => rest)
    const { error: retryErr } = await supabase.from('tasks').insert(withoutShift)
    if (retryErr) return { created: 0, error: retryErr }
    return { created: withoutShift.length, degraded: true }
  }

  return { created: 0, error }
}

/** Postgres 42703 / PostgREST PGRST204 both mean "no such column". */
function isMissingColumn(error, column) {
  if (!error) return false
  const code = error.code || ''
  const msg = `${error.message || ''}`.toLowerCase()
  return (code === '42703' || code === 'PGRST204') && msg.includes(column)
}

/**
 * A task's shift, preferring the column on the task itself.
 *
 * Rows created before the shift column existed have NULL, so the template
 * title match is kept as a fallback — otherwise a caregiver's older tasks
 * would all collapse to "any" the day the migration lands.
 */
export function shiftForTask(task, templates = []) {
  if (task?.shift) return task.shift
  const t = templates.find((x) => x.title === task?.title)
  return t?.shift || 'any'
}

/** NULL and 'any' both mean "show on every shift". */
export function tasksForShift(tasks = [], templates = [], shift) {
  return tasks.filter((task) => {
    const s = shiftForTask(task, templates)
    return s === 'any' || s === shift
  })
}
